import { useContext, useCallback, useMemo } from 'react'

import { cookies as zxAuthCookies } from '@xti-external/frontend-kit-auth/auth'

import { ALLOWED_DOMAINS } from '~/config'
import { ROLE_KEYS } from '~/config/_idm'
import { LOCAL_STORAGE_KEY_USER } from '~/config/constants'
import idmRoutes from '~/routes/_IDM'
import * as LS from '~/utils/localStorage'

import graphqlClient from '../apollo/client'

import { AuthContext } from './AuthProvider'
import { authLogout, decodeJWTToUserData } from './loginHelper'

const useAuth = () => {
  const { isKalista, isLoggedIn, setIsLoggedIn, userData, setUserData } =
    useContext(AuthContext)

  const handleFetchProfile = useCallback(async () => {
    const jwtToken = zxAuthCookies.getToken()?.token

    const userMetaData = await decodeJWTToUserData(jwtToken)

    if (userMetaData) {
      setUserData(userMetaData)
      LS.set(LOCAL_STORAGE_KEY_USER, userMetaData)
    }
  }, [])

  const handleLogin = useCallback(
    async (token, refreshToken) => {
      zxAuthCookies.setToken(token, refreshToken, ALLOWED_DOMAINS)
      setIsLoggedIn(true)

      const userMetaData = await decodeJWTToUserData(token)
      if (userMetaData) {
        setUserData(userMetaData)
        LS.set(LOCAL_STORAGE_KEY_USER, userMetaData)
      }
    },
    [setIsLoggedIn, setUserData]
  )

  const handleLogout = useCallback(async () => {
    await authLogout().catch()
    setIsLoggedIn(false)

    setUserData(null)
    zxAuthCookies.removeToken(ALLOWED_DOMAINS)

    LS.remove(LOCAL_STORAGE_KEY_USER)

    await graphqlClient.clearStore()
  }, [setIsLoggedIn])

  const isPermissionGranted = useCallback(
    (permission) => {
      if (permission === 'ALL') return true
      
      const permissions = userData?.userAccess?.permissionMenu
      if (!permissions) return false
      
      // Handle array of permissions - check if user has ANY of them
      if (Array.isArray(permission)) {
        return permission.some(perm => permissions.includes(perm))
      }
      
      // Handle single permission
      return permissions.includes(permission)
    },
    [userData]
  )

  const userRoleType = useMemo(() => {
    return {
      isSysadmin: userData?.userAccess?.roleType === ROLE_KEYS.SYS_ADMIN,
      isClientAdmin: userData?.userAccess?.roleType === ROLE_KEYS.CLIENT_ADMIN,
      isRegularUser: userData?.userAccess?.roleType === ROLE_KEYS.USER,
    }
  }, [userData])

  const idmRoutesUsers = useMemo(() => {
    const { isSysadmin, isClientAdmin, isRegularUser } = userRoleType

    return idmRoutes.filter((route) => {
      if (route.disabled) return false

      if (isSysadmin && route.sysAdminAccess) return true
      if (isClientAdmin && route.clientAdminAccess) return true
      if (isRegularUser && route.userAccess) return true

      return false
    })
  }, [userRoleType])

  const getUserInformation = useCallback(() => {
    let user
    try {
      user = typeof userData === 'object' ? userData : JSON.parse(userData)
    } catch (e) {} // eslint-disable-line no-empty
    return user
  }, [userData])

  const handleUpdateProfile = useCallback(
    (updatedUserData) => {
      const newUserData = {
        ...userData,
        userName: updatedUserData.userName,
        location: updatedUserData.location,
        phone: updatedUserData.phone,
        mustChangePwd: updatedUserData.mustChangePwd,
      }

      LS.set(LOCAL_STORAGE_KEY_USER, newUserData)
      setUserData(newUserData)
    },
    [userData]
  )

  return {
    getUserInformation,
    handleFetchProfile,
    handleLogin,
    handleLogout,
    isKalista,
    isLoggedIn,
    isPermissionGranted,
    handleUpdateProfile,
    idmRoutesUsers,
    setIsLoggedIn,
    setUserData,
    token: zxAuthCookies.getToken()?.token || '',
    userData,
    userRoleType,
  }
}

export default useAuth
